import React, { useContext } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { ContactContext } from '@components/Contact/Contact.Context'
import Section from '@components/Section'
import media from '@styles/media'

const products = [
  {
    id: 'swasthaid',
    color: '#0a1f3a',
    accent: '#3a8fd4',
    label: 'Healthcare',
    name: 'SwasthaID',
    tagline: "Nepal's first inter-hospital health data network",
    description:
      'A secure, patient-controlled platform for instant medical record sharing across hospitals. SwasthaID eliminates fragmented care by creating a unified health identity for every patient.',
    features: [
      'Instant cross-hospital record access',
      'Patient-controlled data permissions',
      'HIPAA-grade encryption & audit trails',
      'Integrates with existing hospital systems',
    ],
    logo: '/images/apps/swasthaid-logo.png',
    bg: '/images/apps/swasthaid-logo-d.png',
    cta: 'Learn about SwasthaID',
  },
  {
    id: 'revvlow',
    color: '#1a0d08',
    accent: '#e05c2a',
    label: 'Commerce',
    name: 'Revvlow',
    tagline: 'Turn unsold inventory into revenue in real time',
    description:
      'A dynamic pricing engine for cinemas, events, and hospitality. Revvlow fills empty seats without disrupting your core pricing strategy — boosting occupancy and revenue simultaneously.',
    features: [
      'Real-time dynamic pricing engine',
      'Zero impact on primary price tiers',
      'Plug-and-play API integration',
      'Analytics dashboard for yield management',
    ],
    logo: '/images/apps/revvlow-logo.svg',
    bg: '/images/apps/revvlow.jpg',
    cta: 'Learn about Revvlow',
  },
  {
    id: 'osadai',
    color: '#061a0a',
    accent: '#2d7a3a',
    label: 'Wellness',
    name: 'OsadAI',
    tagline: 'AI-driven natural medicine & wellness platform',
    description:
      'OsadAI bridges ancient Ayurvedic wisdom with modern AI formulation science. Our platform delivers scientifically-backed nutraceutical recommendations personalised to individual health profiles.',
    features: [
      'AI-powered Ayurveda formulation',
      'Personalised nutraceutical plans',
      'Research-backed ingredient database',
      'Practitioner & consumer interfaces',
    ],
    logo: null,
    bg: '/images/apps/osadai.png',
    cta: 'Learn about OsadAI',
  },
]

const stats = [
  { value: '3', label: 'Flagship Platforms' },
  { value: '2+', label: 'Countries Active' },
  { value: '100%', label: 'Data Ownership for Patients' },
  { value: 'AI', label: 'Powered Core' },
]

function OsadLogoInline() {
  return (
    <OsadInline>
      <OsadLeaf />
      <span>OsadAI</span>
    </OsadInline>
  )
}

function HomeProducts() {
  const { toggleContact } = useContext(ContactContext)

  return (
    <Wrapper>
      {/* Stats bar */}
      <StatsBar>
        <Section narrow>
          <StatsGrid>
            {stats.map(s => (
              <StatItem key={s.label}>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </StatItem>
            ))}
          </StatsGrid>
        </Section>
      </StatsBar>

      {/* Section header */}
      <Section narrow>
        <SectionHeader>
          <Eyebrow>Our Platforms</Eyebrow>
          <SectionTitle>Three products. One mission.</SectionTitle>
          <SectionSubtitle>
            Building intelligent infrastructure for healthcare, commerce, and
            wellbeing — designed to integrate, not disrupt.
          </SectionSubtitle>
        </SectionHeader>
      </Section>

      {/* Product cards */}
      <Section narrow>
        <CardsGrid>
          {products.map(p => (
            <ProductCard key={p.id} color={p.color} accent={p.accent}>
              <CardTop>
                <CardLabel accent={p.accent}>{p.label}</CardLabel>
                <CardLogo>
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={p.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : (
                    <OsadLogoInline />
                  )}
                </CardLogo>
                <CardName accent={p.accent}>{p.name}</CardName>
                <CardTagline>{p.tagline}</CardTagline>
              </CardTop>
              <CardDivider accent={p.accent} />
              <CardDesc>{p.description}</CardDesc>
              <FeatureList>
                {p.features.map(f => (
                  <FeatureItem key={f} accent={p.accent}>
                    <Dot accent={p.accent} />
                    {f}
                  </FeatureItem>
                ))}
              </FeatureList>
              <CardFooter>
                <CardCta
                  accent={p.accent}
                  onClick={toggleContact}
                >
                  {p.cta} →
                </CardCta>
              </CardFooter>
              <CardBgImage src={p.bg} />
            </ProductCard>
          ))}
        </CardsGrid>
      </Section>

      {/* Mission strip */}
      <MissionStrip>
        <Section narrow>
          <MissionInner>
            <MissionQuote>
              "We prioritize systemic impact over short-term profitability."
            </MissionQuote>
            <MissionBody>
              If you are seeking a mission-driven team dedicated to
              revolutionizing global health through AI and building a sustainable
              future, you have found the right partner.
            </MissionBody>
            <MissionCta onClick={toggleContact}>Get in touch →</MissionCta>
          </MissionInner>
        </Section>
      </MissionStrip>
    </Wrapper>
  )
}

export default HomeProducts

/* ─── Styled components ─────────────────────────────── */

const Wrapper = styled.div`
  background: #08070b;
  padding-bottom: 80px;
`

const StatsBar = styled.div`
  background: #101216;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 32px 0;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  ${media.tablet`
    grid-template-columns: repeat(2, 1fr);
  `}
`

const StatItem = styled.div`
  text-align: center;
`

const StatValue = styled.div`
  font-size: 3.6rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -1px;
`

const StatLabel = styled.div`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`

const SectionHeader = styled.div`
  text-align: center;
  padding: 80px 0 50px;
  max-width: 620px;
  margin: 0 auto;
`

const Eyebrow = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-size: 4.2rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -1px;
  line-height: 1.15;
  margin-bottom: 18px;

  ${media.tablet`
    font-size: 3rem;
  `}
`

const SectionSubtitle = styled.p`
  font-size: 1.7rem;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  ${media.desktop`
    grid-template-columns: 1fr;
    max-width: 560px;
    margin: 0 auto;
  `}
`

const ProductCard = styled.div<{ color: string; accent: string }>`
  position: relative;
  background: ${p => p.color};
  border: 1px solid ${p => p.accent}33;
  border-radius: 12px;
  padding: 40px 36px 36px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 520px;
  transition: border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: ${p => p.accent}88;
    transform: translateY(-4px);
  }
`

const CardTop = styled.div`
  position: relative;
  z-index: 1;
`

const CardLabel = styled.span<{ accent: string }>`
  display: inline-block;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${p => p.accent};
  background: ${p => p.accent}22;
  border-radius: 4px;
  padding: 3px 10px;
  margin-bottom: 20px;
`

const CardLogo = styled.div`
  height: 44px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`

const CardName = styled.h3<{ accent: string }>`
  font-size: 2.8rem;
  font-weight: 700;
  color: ${p => p.accent};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`

const CardTagline = styled.p`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`

const CardDivider = styled.div<{ accent: string }>`
  width: 40px;
  height: 2px;
  background: ${p => p.accent};
  opacity: 0.4;
  margin: 20px 0;
  position: relative;
  z-index: 1;
`

const CardDesc = styled.p`
  font-size: 1.45rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.65;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
  flex: 1;
`

const FeatureItem = styled.li<{ accent: string }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
`

const Dot = styled.span<{ accent: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.accent};
  flex-shrink: 0;
  margin-top: 7px;
`

const CardFooter = styled.div`
  position: relative;
  z-index: 1;
`

const CardCta = styled.button<{ accent: string }>`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${p => p.accent};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.75;
    text-decoration: underline;
  }
`

const CardBgImage = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 55%;
  height: 55%;
  object-fit: cover;
  opacity: 0.07;
  pointer-events: none;
  border-radius: 0 0 12px 0;
`

const MissionStrip = styled.div`
  margin-top: 80px;
  background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 70px 0;
`

const MissionInner = styled.div`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`

const MissionQuote = styled.blockquote`
  font-size: 2.8rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  letter-spacing: -0.5px;
  margin-bottom: 20px;
  font-style: italic;

  ${media.tablet`
    font-size: 2.2rem;
  `}
`

const MissionBody = styled.p`
  font-size: 1.7rem;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.7;
  margin-bottom: 36px;
`

const MissionCta = styled.button`
  font-size: 1.6rem;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 12px 28px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.3);
  }
`

const OsadInline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-size: 2rem;
    font-weight: 700;
    color: #2d7a3a;
    letter-spacing: -0.5px;
  }
`

const OsadLeaf = styled.div`
  width: 28px;
  height: 28px;
  background: #2d7a3a;
  border-radius: 50% 0 50% 0;
  transform: rotate(45deg);
  flex-shrink: 0;
`
